import "./SectionOwnersModal.less";

import { Button, Modal, Spin, Table } from "antd";
import { ColumnProps } from "antd/lib/table";
import React from "react";

import { PermissionApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { UserApi } from "lib/api/security/user";
import { PermissibleUser, Permission, Section } from "lib/data/model";
import { User } from "lib/data/model";
import { Right } from "lib/data/model/enumerations";
import { SingleRecordPermission } from "lib/data/model/security/SingleRecordPermission";
import { StateService, UtilityService } from "lib/services";


import { DeleteButton } from "components/general/StandardButtons";
import { NewRowItem } from "./NewRowItem/NewRowItem";
import { orderBy } from "lodash";


interface ISectionOwnersModalProps {
	closeModal: () => void;
	selected: boolean;
	section: Section;
}

interface ISectionOwnersModalState {
	isLoading: boolean;
	isAwaitingUpdate: boolean;
}

export class SectionOwnersModal extends React.Component<ISectionOwnersModalProps, ISectionOwnersModalState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	private sectionUsers: PermissibleUser[] = [];
	private users: User[] = [];

	private newRowItemRef = React.createRef<NewRowItem>();

	private columns: Array<ColumnProps<PermissibleUser>> = [
		{
			dataIndex: "user.username",
			title: "Username"
		},
		{
			title: "",
			render: (permissibleUser: PermissibleUser) => (permissibleUser.isPermissionExact ? <DeleteButton onClick={() => this.deleteUser(permissibleUser.user)} /> : null),
			align: "center"
		}
	];

	constructor(props: ISectionOwnersModalProps) {

		super(props);

		this.state = {
			isLoading: true,
			isAwaitingUpdate: false
		};
	}

	public async componentWillMount() {

		// Initialize Component
		await this.setupSectionUsers();
		await this.setupAllUsers();

		// Indicate Component Is NOT Loading
		StateService.setCancelableState(() => this.setState({ isLoading: false }), this.apiCancellationToken);
	}

	public async componentWillReceiveProps() {

		// Indicate Component Is Loading
		StateService.setCancelableState(() => this.setState({ isLoading: true }), this.apiCancellationToken);

		// Re-Initialize Component
		await this.setupSectionUsers();
		await this.setupAllUsers();

		// Indicate Component Is NOT Loading
		StateService.setCancelableState(() => this.setState({ isLoading: false }), this.apiCancellationToken);
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		let userManagementModalTitle: string;

		if (this.props.section.published !== undefined) {

			userManagementModalTitle = this.props.section.published.name;
		}
		else if (this.props.section.draft !== undefined) {

			userManagementModalTitle = this.props.section.draft.name;
		}
		else {

			userManagementModalTitle = "Section draft and published version missing";
		}

		return (

			<div className="section-owners-modal">
				<Modal
					visible={this.props.selected}
					className="user-rights-wrapper"
					title={userManagementModalTitle}
					onCancel={this.props.closeModal}
					footer={[
						<Button key="section-owners-modal-close" onClick={this.props.closeModal}>Close</Button>
					]}
				>
					{(this.state.isLoading
						?
						<Spin />
						:
						<React.Fragment>
							<NewRowItem
								ref={this.newRowItemRef}
								addNewUser={this.addNewUser}
								disabled={this.state.isAwaitingUpdate}
								users={
									this.users.filter(
										(user) => this.sectionUsers.find(
											(sectionUser) => sectionUser.user.id === user.id
										) === undefined
									)
								}
							/>

							<Table
								loading={this.state.isAwaitingUpdate}
								pagination={false}
								columns={this.columns}
								dataSource={this.sortSectionUsers(this.sectionUsers)}
								rowKey={(record) => record.user.id!.toString()}
								bordered={true}
							/>
						</React.Fragment>
					)}
				</Modal>
			</div>
		);
	}

	private sortSectionUsers = (sectionUsers: PermissibleUser[]): PermissibleUser[] => {

		return orderBy(sectionUsers, [(user) => user.user.username.toLowerCase()], ["asc"]);
	}

	private setupSectionUsers = async () => {

		// Call the permissions api with the section id
		const users = await PermissionApi.getPermittedSectionUsers(Right.Update, this.props.section);

		// Store Permissible Users
		this.sectionUsers = users;

		this.forceUpdate();
	}

	private setupAllUsers = async () => {

		// Call the users api to get all the users
		const users = await UserApi.getAllUsers();

		if (users) {

			this.users = users;

			this.forceUpdate();
		}
	}

	private deleteUser = async (user: User) => {

		if (this.props.section.id) {

			StateService.setCancelableState(() => this.setState({ isAwaitingUpdate: true }), this.apiCancellationToken);

			const permission: Permission = new SingleRecordPermission(Right.Update, this.props.section.$type, this.props.section.id);

			await UtilityService.asyncTimeout(PermissionApi.deleteUserPermission(user, permission));

			this.sectionUsers = this.sectionUsers.filter((sectionUser) => sectionUser.user.id !== user.id);

			StateService.setCancelableState(() => this.setState({ isAwaitingUpdate: false }), this.apiCancellationToken);
		}
	}

	private addNewUser = async (user: User): Promise<boolean> => {

		let userAddedToSection: boolean = false;

		// if the user is already part of the section return false
		for (const sectionUser of this.sectionUsers) {

			if (sectionUser.user.id === user.id) {

				//    TODO retun an enumuration to signify what type of error to display
				return false;
			}
		}

		StateService.setCancelableState(() => this.setState({ isAwaitingUpdate: true }), this.apiCancellationToken);

		if (this.props.section.id) {

			// create permission
			const permission: Permission = new SingleRecordPermission(Right.Update, this.props.section.$type, this.props.section.id);

			//  execute permission
			const userAssignment = await UtilityService.asyncTimeout(PermissionApi.assignUserPermission(user, permission));

			if (userAssignment) {

				// Attempt to reset select after successful save
				if (this.newRowItemRef.current) {

					this.newRowItemRef.current.resetSelectValue();
				}

				// Construct Permissible User
				const permissibleUser = new PermissibleUser();
				permissibleUser.user = user;
				permissibleUser.isPermissionExact = true;

				// set return as success
				userAddedToSection = true;
				this.sectionUsers.push(permissibleUser);
				this.forceUpdate();
			}
		}

		StateService.setCancelableState(() => this.setState({ isAwaitingUpdate: false }), this.apiCancellationToken);

		return userAddedToSection;
	}
}
